/**
 * Remembers which method finished the previous successful authentication on
 * this browser, so the login screen can point a returning user back at it.
 *
 * This is a UI hint and nothing else — it never gates, pre-fills or authorises
 * anything, so a tampered or stale value can only produce a wrong badge. It is
 * kept in browser storage (which survives the redirect out to an IDP and back)
 * as a small JSON envelope.
 *
 * There is deliberately NO expiry. The hint lives as long as the browser keeps
 * it, and the browser's own rules — clearing site data, private mode, storage
 * eviction — are the only thing that ends it. A returning user is a returning
 * user whether they were last here on Tuesday or a year ago, and an expiry
 * would silently withhold the hint from exactly the people most likely to have
 * forgotten which method they used.
 *
 * The write timestamp is still recorded, but only as data: it is reported to
 * BI so the experiment can ask whether people act on old hints as readily as
 * on fresh ones. It never invalidates a hint.
 */

export enum AuthMethod {
  EMAIL = 'email',
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  APPLE = 'apple',
  SSO = 'sso',
}

export const LAST_USED_AUTH_METHOD_KEY = 'wixLastUsedAuthMethod';

const KNOWN_METHODS: ReadonlySet<string> = new Set(Object.values(AuthMethod));

const PROVIDER_ID_TO_AUTH_METHOD: Readonly<Record<string, AuthMethod>> = {
  google: AuthMethod.GOOGLE,
  facebook: AuthMethod.FACEBOOK,
  apple: AuthMethod.APPLE,
};

interface StoredHint {
  method: AuthMethod;
  at: number;
}

// localStorage is absent in non-browser environments and throws outright in
// Safari private mode / when cookies are blocked. Every access is therefore
// best-effort and silent: no hint simply means no badge, which is the correct
// behaviour, and an error log on the login screen would be pure noise.
const getStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};

const readHint = (): AuthMethod | null => {
  const raw = (() => {
    try {
      return getStorage()?.getItem(LAST_USED_AUTH_METHOD_KEY) ?? null;
    } catch {
      return null;
    }
  })();
  if (!raw) {
    return null;
  }
  let parsed: Partial<StoredHint>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Anything we cannot parse (including the plain-string format an earlier
    // iteration of this feature wrote) is treated as absent.
    return null;
  }
  // Only the method is validated. `at` is informational, so a hint written by
  // a version that recorded it differently — or not at all — still counts.
  const { method, at } = parsed;
  if (typeof method !== 'string' || !KNOWN_METHODS.has(method)) {
    return null;
  }
  cachedWrittenAt =
    typeof at === 'number' && Number.isFinite(at) ? at : undefined;
  return method as AuthMethod;
};

// The hint changes at most once per page load (a write is always followed by a
// redirect away), so it is read from storage once and memoised. That keeps
// mobx-observed components free of a synchronous storage read on every render.
let cachedHint: AuthMethod | null | undefined;
let cachedWrittenAt: number | undefined;

export const getLastUsedAuthMethod = (): AuthMethod | null => {
  if (cachedHint === undefined) {
    cachedHint = readHint();
  }
  return cachedHint;
};

/**
 * Whole days since the hint was written, or undefined when there is no hint or
 * it carries no usable timestamp.
 *
 * Reported to BI, never used to gate the badge. With no expiry in place this
 * is how the experiment can tell whether a hint from last week and one from
 * two years ago are acted on at the same rate — and therefore whether an
 * expiry would ever have been worth having.
 */
export const getLastUsedAuthMethodAgeDays = (): number | undefined => {
  if (getLastUsedAuthMethod() === null || cachedWrittenAt === undefined) {
    return undefined;
  }
  const elapsed = Date.now() - cachedWrittenAt;
  return elapsed < 0 ? 0 : Math.floor(elapsed / (24 * 60 * 60 * 1000));
};

export const setLastUsedAuthMethod = (method: AuthMethod): void => {
  cachedHint = method;
  cachedWrittenAt = Date.now();
  const hint: StoredHint = { method, at: cachedWrittenAt };
  try {
    getStorage()?.setItem(LAST_USED_AUTH_METHOD_KEY, JSON.stringify(hint));
  } catch {
    // Storage is unavailable or full — the badge is simply not shown next time.
  }
};

export const clearLastUsedAuthMethod = (): void => {
  cachedHint = null;
  cachedWrittenAt = undefined;
  try {
    getStorage()?.removeItem(LAST_USED_AUTH_METHOD_KEY);
  } catch {
    // Nothing to do. The hint has no expiry, so a failed clear leaves it in
    // place — acceptable for a UI hint, and the in-memory value is already
    // cleared above.
  }
};

/** Test-only: forget the memoised value so the next read hits storage again. */
export const resetLastUsedAuthMethodCache = (): void => {
  cachedHint = undefined;
  cachedWrittenAt = undefined;
};

/**
 * Maps a social store's `providerId` onto the stored method. Returns undefined
 * for providers we do not badge (custom IDP connections, for instance), and
 * callers skip the write in that case rather than storing something unusable.
 */
export const authMethodFromProviderId = (
  providerId: string,
): AuthMethod | undefined =>
  PROVIDER_ID_TO_AUTH_METHOD[providerId?.toLowerCase?.() ?? ''];
